import { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Toast from 'react-native-toast-message';

import { Button, Text, UserAvatar, spacing, useConfirm, ActionsMenu } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { useAuthHeaders } from '@pletly/feature-auth';

import { MembersTable } from '../../../components/MembersTable';
import { TemporaryAccessTableDataType } from '../useOrganisationData';
import { useRemoveAppInvitationMutation } from './__generated__/removeAppInvitation.mutation.generated';
import { GetOrganisationDocument } from '../__generated__/organisation.generated';
import { useRemoveTemporaryConnectionMutation } from './__generated__/removeTemporaryConnection.mutation.generated';
import { EmailCell } from '../EmailCell';
import { useGoToProfile } from '../../../components/useGoToProfile';
import { UsersContainer } from '../UsersContainer';

interface TemporaryAccessTableProps {
	organisationUuid: string;
	data: TemporaryAccessTableDataType[];
}

export const TemporaryAccessTable = ({ organisationUuid, data }: TemporaryAccessTableProps) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const { confirm } = useConfirm();
	const [removeAppInvitationMutation, { loading: removeAppInvitationLoading }] =
		useRemoveAppInvitationMutation({
			refetchQueries: [GetOrganisationDocument],
		});
	const [removeTemporaryConnectionMutation, { loading: removeTemporaryConnectionLoading }] =
		useRemoveTemporaryConnectionMutation({
			refetchQueries: [GetOrganisationDocument],
		});

	const loading = useMemo(
		() => removeAppInvitationLoading || removeTemporaryConnectionLoading,
		[removeAppInvitationLoading, removeTemporaryConnectionLoading],
	);

	const getActionByType = useCallback(
		(row: TemporaryAccessTableDataType) => {
			switch (row.type) {
				case 'appInvitation':
					return () => removeAppInvitationMutation({ variables: { id: row.appInvitationId } });
				case 'temporaryConnection':
					return () =>
						removeTemporaryConnectionMutation({
							variables: {
								uuid: row.temporaryConnectionUuid,
							},
						});
				default:
					throw new Error('Unknown type');
			}
		},
		[removeAppInvitationMutation, removeTemporaryConnectionMutation],
	);

	const handleRevokeAccess = useCallback(
		async (row: TemporaryAccessTableDataType) => {
			if (loading) return;

			const confirmation = await confirm(
				t('confirmations.revoke-temporary-access.title'),
				t('confirmations.revoke-temporary-access.content', {
					caregiverEmail: row.caregiver.email,
					careRecipientName: getName(row.careRecipient),
				}),
			);

			if (!confirmation) {
				return;
			}

			const action = getActionByType(row);
			try {
				await action();
				Toast.show({
					text1: t('toasts.success.temporary-access-revoked'),
				});
			} catch (error) {
				Toast.show({
					type: 'error',
					text1: t('toasts.error.something-wrong'),
				});
			}
		},
		[getActionByType, t, loading, confirm],
	);

	return (
		<UsersContainer>
			<UsersContainer.Header>
				<Button
					style={styles.newButton}
					variant="filled"
					color="primary"
					onPress={() => navigate('NewTemporaryAccess', { organisationUuid })}
				>
					{t('temporary-access.new-button')}
				</Button>
			</UsersContainer.Header>

			<MembersTable
				data={data}
				renderRowActions={({ row }) => (
					<ActionsMenu
						actions={[
							{
								title: t('table.actions.revoke-access'),
								onPress: () => handleRevokeAccess(row),
							},
						]}
					/>
				)}
				columnsConfig={[
					{
						name: t('table.header.email'),
						renderCell: ({ row: { caregiver } }) => <EmailCell email={caregiver.email} />,
					},
					{
						name: t('table.header.care-recipient'),
						renderCell: ({ row: { careRecipient } }) => (
							<CareRecipientCell careRecipient={careRecipient} />
						),
					},
					{
						name: t('table.header.access-end-date'),
						renderCell: ({ row: { endDate } }) => (
							<Text category="p1">{format(endDate, 'do MMMM yyyy')}</Text>
						),
					},
				]}
			/>
		</UsersContainer>
	);
};

const CareRecipientCell = ({
	careRecipient,
}: {
	careRecipient: TemporaryAccessTableDataType['careRecipient'];
}) => {
	const { getHeaders } = useAuthHeaders();
	const goToProfile = useGoToProfile();

	const photoSource = careRecipient.photoUrl
		? { uri: careRecipient.photoUrl, headers: getHeaders(careRecipient.photoUrl) }
		: undefined;

	return (
		<Pressable style={styles.careRecipientCell} onPress={() => goToProfile(careRecipient)}>
			<UserAvatar size={32} username={getName(careRecipient)} source={photoSource} />
			<Text category="p1">{getName(careRecipient)}</Text>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	newButton: {
		alignSelf: 'flex-end',
	},
	careRecipientCell: {
		flexDirection: 'row',
		gap: spacing(),
		alignItems: 'center',
	},
});
